import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import {
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { MODULE_LABELS, ROLE_LABELS } from "../constants/rbac";
import { formatDate } from "../utils/helpers";

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.25 }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const StaffDetailsModal = ({ open, onClose, staff }) => {
  if (!staff) return null;

  const initials = staff.name
    ? staff.name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    : "ST";

  const roleLabel = ROLE_LABELS[staff.role] || "Staff";
  const enabledModules = Object.entries(staff.modules || {})
    .filter(([, enabled]) => enabled === true)
    .map(([moduleKey]) => MODULE_LABELS[moduleKey] || moduleKey);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Staff Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
          <Box
            sx={{
              minWidth: { xs: "100%", md: 240 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Avatar
              src={staff.photoURL || ""}
              sx={{
                width: 120,
                height: 120,
                fontSize: 40,
                fontWeight: 700,
                bgcolor: "#f5f5f5",
                border: "3px solid #e0e0e0",
                mb: 1.5,
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {staff.name || "Staff User"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {staff.email || "No email"}
            </Typography>
            <Chip label={roleLabel} color="primary" size="small" sx={{ fontWeight: 600 }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<EmailIcon sx={{ color: "#DD2424", fontSize: 20, mt: 0.25 }} />}
                  label="Email"
                  value={staff.email || "N/A"}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<BadgeIcon sx={{ color: "#DD2424", fontSize: 20, mt: 0.25 }} />}
                  label="Role"
                  value={roleLabel}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<SecurityIcon sx={{ color: "#DD2424", fontSize: 20, mt: 0.25 }} />}
                  label="Enabled Modules"
                  value={String(enabledModules.length)}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<CalendarIcon sx={{ color: "#DD2424", fontSize: 20, mt: 0.25 }} />}
                  label="Created"
                  value={staff.createdAt ? formatDate(staff.createdAt) : "N/A"}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<CalendarIcon sx={{ color: "#DD2424", fontSize: 20, mt: 0.25 }} />}
                  label="Updated"
                  value={staff.updatedAt ? formatDate(staff.updatedAt) : "N/A"}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 1 }}>
              Module Access
            </Typography>
            {enabledModules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No modules enabled.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {enabledModules.map((moduleName) => (
                  <Chip key={moduleName} label={moduleName} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffDetailsModal;